import {apiClient} from 'common/util/api-client';
import {getProjectId} from 'common/report/util';

// Mirrors the server-enforced cap.
export const MAX_CONVERSATION_TITLE_LENGTH = 255;

export type ConversationSummary = {
  id: string;
  title: string;
  // ISO-8601 with an explicit zone designator.
  created_at: string;
  updated_at: string;
};

type ConversationDetail = ConversationSummary & {
  messages: unknown[];
  // The conversation owner's user id (stringified), not the requesting user's.
  user_id: string;
  is_shared: boolean;
};

function baseUrl() {
  return `/api/app/projects/${getProjectId()}/agent/conversations`;
}

export type GetConversationOptions = {
  sharedNumMessages?: number;
};

function detailUrl(id: string, {sharedNumMessages}: GetConversationOptions = {}) {
  const url = `${baseUrl()}/${id}`;
  return sharedNumMessages === undefined ? url : `${url}?num_messages=${sharedNumMessages}`;
}

// Shared so every caller keys off the exact same react-query cache entry.
export function copilotConversationsQueryKey(projectId: number) {
  return [`copilot-conversations`, projectId] as const;
}

export async function listConversations(): Promise<ConversationSummary[]> {
  return apiClient.get<ConversationSummary[]>(baseUrl());
}

export async function getConversation(id: string, options: GetConversationOptions = {}): Promise<ConversationDetail> {
  return apiClient.get<ConversationDetail>(detailUrl(id, options));
}

export async function renameConversation(id: string, title: string): Promise<{title: string}> {
  return apiClient.patch<{title: string}>(detailUrl(id), {title});
}

export async function deleteConversation(id: string): Promise<{id: string}> {
  return apiClient.delete(detailUrl(id));
}

export async function shareConversation(id: string): Promise<{id: string; is_shared: boolean}> {
  return apiClient.patch<{id: string; is_shared: boolean}>(detailUrl(id), {is_shared: true});
}

export async function unshareConversation(id: string): Promise<{id: string; is_shared: boolean}> {
  return apiClient.patch<{id: string; is_shared: boolean}>(detailUrl(id), {is_shared: false});
}
